/**
 * Manages preset selection and color/text application for the ribbon pin customizer.
 */

import { CustomizerEngine } from 'smg/customizer';
import { RibbonTextLayer } from './ribbon-text-layer';
import { ColorState } from './color-state';

export interface PresetColors {
    colorOne: string;
    colorTwo: string;
    colorThree: string;
}

export interface PresetTextColors {
    textOne: string;
    textTwo: string;
    textThree: string;
}

export interface PresetFontSizes {
    textOne: number;
    textTwo: number;
    textThree: number;
}

export const defaultFontSizes: PresetFontSizes = { textOne: 7, textTwo: 6, textThree: 8 };

export interface MonthPreset {
    month: string;
    name: string;
    lineOne: string;
    lineTwo: string;
    colors: PresetColors;
    textColors: PresetTextColors;
    fontSizes: PresetFontSizes;
}

export const presets: MonthPreset[] = [
    {
        month: 'January',
        name: 'Cervical Cancer Awareness',
        lineOne: 'Cervical Cancer',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'FFFFFF',
            colorTwo: '1D9391',
            colorThree: '00B8B4',
        },
        textColors: {
            textOne: '1D9391',
            textTwo: '1D9391',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'February',
        name: 'Heart Disease Awareness',
        lineOne: 'Heart Disease',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'CE102C',
            colorTwo: 'CE102C',
            colorThree: '79242E',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: { textOne: 8, textTwo: 6, textThree: 8 },
    },
    {
        month: 'March',
        name: 'Kidney Cancer Awareness',
        lineOne: 'Kidney Cancer',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'FF681D',
            colorTwo: 'FF681D',
            colorThree: 'D64123',
        },
        textColors: {
            textOne: '004988',
            textTwo: '004988',
            textThree: '004988',
        },
        fontSizes: { textOne: 9, textTwo: 6, textThree: 8 },
    },
    {
        month: 'April',
        name: 'Animal Abuse Awareness',
        lineOne: 'Animal Abuse',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: '60269E',
            colorTwo: '60269E',
            colorThree: '9263CD',
        },
        textColors: {
            textOne: 'D8C5E6',
            textTwo: 'D8C5E6',
            textThree: 'D8C5E6',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'May',
        name: 'Mental Health Awareness',
        lineOne: 'Mental Health',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: '008A18',
            colorTwo: '008A18',
            colorThree: '006242',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'June',
        name: 'Cancer Survivor Awareness',
        lineOne: 'Cancer Survivor',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: '8232A7',
            colorTwo: '8232A7',
            colorThree: 'B780D0',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'July',
        name: 'Sarcoma Cancer Awareness',
        lineOne: 'Sarcoma Cancer',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'FCE020',
            colorTwo: 'FCE020',
            colorThree: 'F6B700',
        },
        textColors: {
            textOne: '724722',
            textTwo: '724722',
            textThree: '724722',
        },
        fontSizes: { textOne: 6, textTwo: 6, textThree: 8 },
    },
    {
        month: 'August',
        name: 'Appendix Cancer Awareness',
        lineOne: 'Appendix Cancer',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'F6A800',
            colorTwo: 'F6A800',
            colorThree: 'F5CE3E',
        },
        textColors: {
            textOne: '0085AE',
            textTwo: '0085AE',
            textThree: '0085AE',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'September',
        name: 'Leukemia Awareness',
        lineOne: 'Leukemia',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'FF9016',
            colorTwo: 'FF9016',
            colorThree: 'FFAE60',
        },
        textColors: {
            textOne: 'B4221B',
            textTwo: 'B4221B',
            textThree: 'B4221B',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'October',
        name: 'Breast Cancer Awareness',
        lineOne: 'Breast Cancer',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'F361A2',
            colorTwo: 'F361A2',
            colorThree: 'F695C5',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'November',
        name: 'Diabetes Awareness',
        lineOne: 'Diabetes',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: '479DC8',
            colorTwo: '479DC8',
            colorThree: '316195',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
    {
        month: 'December',
        name: 'HIV/AIDS Awareness',
        lineOne: 'HIV/AIDS',
        lineTwo: 'Awareness Month',
        colors: {
            colorOne: 'D40019',
            colorTwo: 'D40019',
            colorThree: 'B02028',
        },
        textColors: {
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        },
        fontSizes: defaultFontSizes,
    },
];

interface presetManagerConfig {
    engine: CustomizerEngine;
    colorState: ColorState;
    presets: MonthPreset[];
    defaultFontSizes: PresetFontSizes;
    shiftColor: (hexColor: string, double?: boolean) => string;
}

export class PresetManager {
    readonly #engine: CustomizerEngine;
    readonly #colorState: ColorState;
    readonly #presets: MonthPreset[];
    readonly #defaultFontSizes: PresetFontSizes;
    readonly #shiftColor: (hexColor: string, double?: boolean) => string;

    // DOM elements - color pickers
    readonly #$color1Picker: HTMLInputElement | null;
    readonly #$color2Picker: HTMLInputElement | null;
    readonly #$color3Picker: HTMLInputElement | null;

    // DOM elements - text inputs
    readonly #$textOneInput: HTMLInputElement | null;
    readonly #$textTwoInput: HTMLInputElement | null;
    readonly #$textThreeInput: HTMLInputElement | null;

    // DOM elements - text color pickers
    readonly #$textOneColorPicker: HTMLInputElement | null;
    readonly #$textTwoColorPicker: HTMLInputElement | null;
    readonly #$textThreeColorPicker: HTMLInputElement | null;

    // DOM elements - dropdown
    readonly #$monthSelect: HTMLElement | null;
    readonly #$monthSelectMenu: HTMLElement | null;
    readonly #$monthSelectBtn: HTMLButtonElement | null;

    // Color blocks for the Custom option (tracked for updates)
    readonly #customMenuColorBlocks: HTMLDivElement[] = [];
    readonly #customBtnColorBlocks: HTMLDivElement[] = [];

    constructor(config: presetManagerConfig) {
        this.#engine = config.engine;
        this.#colorState = config.colorState;
        this.#presets = config.presets;
        this.#defaultFontSizes = config.defaultFontSizes;
        this.#shiftColor = config.shiftColor;

        // Get DOM element references
        this.#$color1Picker = document.querySelector<HTMLInputElement>('#ribbon-color-1-swatch');
        this.#$color2Picker = document.querySelector<HTMLInputElement>('#ribbon-color-2-swatch');
        this.#$color3Picker = document.querySelector<HTMLInputElement>('#ribbon-color-3-swatch');

        this.#$textOneInput = document.querySelector<HTMLInputElement>('#text-one-input');
        this.#$textTwoInput = document.querySelector<HTMLInputElement>('#text-two-input');
        this.#$textThreeInput = document.querySelector<HTMLInputElement>('#text-three-input');

        this.#$textOneColorPicker = document.querySelector<HTMLInputElement>('#color-text-one-input');
        this.#$textTwoColorPicker = document.querySelector<HTMLInputElement>('#color-text-two-input');
        this.#$textThreeColorPicker = document.querySelector<HTMLInputElement>('#color-text-three-input');

        this.#$monthSelect = document.querySelector<HTMLElement>('#month-preset-selector');
        this.#$monthSelectMenu = this.#$monthSelect?.querySelector<HTMLElement>('.dropdown-menu') ?? null;
        this.#$monthSelectBtn = this.#$monthSelect?.querySelector<HTMLButtonElement>('.dropdown-toggle') ?? null;
    }

    /**
     * Initialize the preset dropdown and event listeners
     */
    initialize(): void {
        this.#setupDropdown();
        this.#setupColorChangeListeners();
        this.#setupResetButton();
    }

    // ==================== Current State Getters ====================

    getCurrentColors(): PresetColors {
        return {
            colorOne: (this.#$color1Picker?.value ?? '#e74c3c').replace('#', ''),
            colorTwo: (this.#$color2Picker?.value ?? '#e74c3c').replace('#', ''),
            colorThree: (this.#$color3Picker?.value ?? '#e74c3c').replace('#', ''),
        };
    }

    getCurrentText(): { lineOne: string; lineTwo: string } {
        return {
            lineOne: this.#$textOneInput?.value ?? '',
            lineTwo: this.#$textTwoInput?.value ?? '',
        };
    }

    getCurrentTextColors(): PresetTextColors {
        return {
            textOne: (this.#$textOneColorPicker?.value ?? '#ffffff').replace('#', ''),
            textTwo: (this.#$textTwoColorPicker?.value ?? '#ffffff').replace('#', ''),
            textThree: (this.#$textThreeColorPicker?.value ?? '#ffffff').replace('#', ''),
        };
    }

    getCurrentFontSizes(): PresetFontSizes {
        const textOne = this.#engine.getLayer('text-one') as RibbonTextLayer | undefined;
        const textTwo = this.#engine.getLayer('text-two') as RibbonTextLayer | undefined;
        const textThree = this.#engine.getLayer('text-three') as RibbonTextLayer | undefined;
        return {
            textOne: textOne?.getFont().size ?? this.#defaultFontSizes.textOne,
            textTwo: textTwo?.getFont().size ?? this.#defaultFontSizes.textTwo,
            textThree: textThree?.getFont().size ?? this.#defaultFontSizes.textThree,
        };
    }

    // ==================== Apply Methods ====================

    applyColors(colors: PresetColors): void {
        const colorMap = [
            { $picker: this.#$color1Picker, color: colors.colorOne },
            { $picker: this.#$color2Picker, color: colors.colorTwo },
            { $picker: this.#$color3Picker, color: colors.colorThree },
        ];

        this.#colorState.isProgrammaticColorChange = true;
        for (const { $picker, color } of colorMap) {
            if ($picker) {
                $picker.value = `#${color}`;
                $picker.dispatchEvent(new Event('input', { bubbles: true }));
            }
        }
        this.#colorState.isProgrammaticColorChange = false;
    }

    applyText(text: { lineOne: string; lineTwo: string; lineThree?: string }): void {
        if (this.#$textOneInput) {
            this.#$textOneInput.value = text.lineOne;
            this.#$textOneInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (this.#$textTwoInput) {
            this.#$textTwoInput.value = text.lineTwo;
            this.#$textTwoInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
        if (text.lineThree !== undefined && this.#$textThreeInput) {
            this.#$textThreeInput.value = text.lineThree;
            this.#$textThreeInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
    }

    applyTextColors(textColors: PresetTextColors): void {
        const colorMap = [
            { $picker: this.#$textOneColorPicker, color: textColors.textOne, lineId: 'text-one' },
            { $picker: this.#$textTwoColorPicker, color: textColors.textTwo, lineId: 'text-two' },
            { $picker: this.#$textThreeColorPicker, color: textColors.textThree, lineId: 'text-three' },
        ];

        for (const { $picker, color, lineId } of colorMap) {
            if ($picker) {
                this.#colorState.beginProgrammaticTextColorChange(lineId);
                $picker.value = `#${color}`;
                $picker.dispatchEvent(new Event('input', { bubbles: true }));
                this.#colorState.endProgrammaticTextColorChange(lineId);
            }
        }
    }

    applyFontSizes(fontSizes: PresetFontSizes): void {
        const layerMap = [
            { layerName: 'text-one', size: fontSizes.textOne },
            { layerName: 'text-two', size: fontSizes.textTwo },
            { layerName: 'text-three', size: fontSizes.textThree },
        ];

        for (const { layerName, size } of layerMap) {
            const layer = this.#engine.getLayer(layerName) as RibbonTextLayer | undefined;
            if (layer) {
                void layer.setFont({ size });
            }
        }
    }

    // ==================== Preset Selection ====================

    selectPreset(preset: MonthPreset): void {
        // Save current state before switching to preset
        if (this.#colorState.isCustomMode) {
            this.#saveCustomState();
            this.#updateCustomMenuToSavedState();
        }

        this.#colorState.isCustomMode = false;
        this.applyColors(preset.colors);
        this.applyText({ lineOne: preset.lineOne, lineTwo: preset.lineTwo });
        this.applyTextColors(preset.textColors);
        this.applyFontSizes(preset.fontSizes);
        this.#updateDropdownPreview(preset);
    }

    selectCustom(): void {
        this.#colorState.isCustomMode = true;

        // Restore saved custom state if available
        if (this.#colorState.savedCustomState) {
            const saved = this.#colorState.savedCustomState;
            this.applyColors(saved.colors);
            this.applyText(saved.text);
            this.applyTextColors(saved.textColors);
            this.applyFontSizes(saved.fontSizes);
            this.#colorState.restoreManualTracking();
        }

        this.#updateDropdownForCustom();
    }

    resetDesign(): void {
        // Default color is red (#e74c3c), with derivatives for color-2 and color-3
        const defaultColor1 = 'e74c3c';
        const defaultColor2 = this.#shiftColor(defaultColor1, false).replace('#', '');
        const defaultColor3 = this.#shiftColor(defaultColor1, true).replace('#', '');

        // Clear all tracking
        this.#colorState.clearManualTracking();
        this.#colorState.clearSavedState();

        // Apply default colors
        this.#colorState.isProgrammaticColorChange = true;
        this.applyColors({
            colorOne: defaultColor1,
            colorTwo: defaultColor2,
            colorThree: defaultColor3,
        });
        this.#colorState.isProgrammaticColorChange = false;

        // Reset text to defaults (line three is current year)
        this.applyText({
            lineOne: 'Your Text Here',
            lineTwo: 'Your Text Here',
            lineThree: new Date().getFullYear().toString(),
        });

        // Reset text colors to white
        this.applyTextColors({
            textOne: 'FFFFFF',
            textTwo: 'FFFFFF',
            textThree: 'FFFFFF',
        });

        // Reset font sizes to defaults
        this.applyFontSizes(this.#defaultFontSizes);

        // Set to custom mode and update dropdown
        this.#colorState.isCustomMode = true;
        this.#updateDropdownForCustom();
    }

    // ==================== Private: State Management ====================

    #saveCustomState(): void {
        this.#colorState.saveState({
            colors: this.getCurrentColors(),
            text: this.getCurrentText(),
            textColors: this.getCurrentTextColors(),
            fontSizes: this.getCurrentFontSizes(),
        });
    }

    // ==================== Private: Dropdown UI ====================

    #setupDropdown(): void {
        if (!this.#$monthSelectMenu || !this.#$monthSelectBtn) {
            return;
        }

        // Set initial button to Custom with color blocks
        this.#updateDropdownForCustom();

        this.#$monthSelectMenu.innerHTML = '';

        // Add "Custom" option at the top
        const $customLi = document.createElement('li');
        const $customA = document.createElement('a');
        $customA.href = '#';
        $customA.className = 'dropdown-item';
        $customA.appendChild(this.#createTrackedColorBlocks(this.#customMenuColorBlocks));
        const $customTextSpan = document.createElement('span');
        $customTextSpan.textContent = 'Custom';
        $customA.appendChild($customTextSpan);
        $customLi.appendChild($customA);
        this.#$monthSelectMenu.appendChild($customLi);

        $customA.addEventListener('click', e => {
            e.preventDefault();
            this.selectCustom();
        });

        // Add preset options
        for (const preset of this.#presets) {
            const $li = document.createElement('li');
            const $a = document.createElement('a');
            $a.href = '#';
            $a.className = 'dropdown-item';
            $a.appendChild(this.#createColorBlocks(preset.colors));

            const $textContainer = document.createElement('div');
            $textContainer.className = 'preset-text';
            const $monthSpan = document.createElement('div');
            $monthSpan.className = 'preset-month d-block d-md-inline';
            $monthSpan.textContent = preset.month;
            const $separator = document.createElement('div');
            $separator.className = 'd-none d-md-inline mx-1';
            $separator.textContent = '-';
            const $nameSpan = document.createElement('div');
            $nameSpan.className = 'preset-name d-block d-md-inline';
            $nameSpan.textContent = preset.name;
            $textContainer.appendChild($monthSpan);
            $textContainer.appendChild($separator);
            $textContainer.appendChild($nameSpan);
            $a.appendChild($textContainer);

            $li.appendChild($a);
            this.#$monthSelectMenu.appendChild($li);

            $a.addEventListener('click', e => {
                e.preventDefault();
                this.selectPreset(preset);
            });
        }
    }

    #setupColorChangeListeners(): void {
        // Listen for color changes to update custom color blocks and handle preset->custom transition
        for (const $picker of [this.#$color1Picker, this.#$color2Picker, this.#$color3Picker]) {
            $picker?.addEventListener('input', () => {
                // If user manually changes color while in preset mode, switch to custom
                if (!this.#colorState.isCustomMode && !this.#colorState.isProgrammaticColorChange) {
                    this.#colorState.isCustomMode = true;
                    this.#saveCustomState();
                    this.#updateDropdownForCustom();
                }
                this.#updateCustomColorBlocks();
            });
        }
    }

    #setupResetButton(): void {
        const $resetBtn = document.querySelector<HTMLElement>('#reset-pin-design');
        $resetBtn?.addEventListener('click', () => {
            this.resetDesign();
        });
    }

    #updateDropdownForCustom(): void {
        if (!this.#$monthSelectBtn) {
            return;
        }
        this.#$monthSelectBtn.innerHTML = '';
        this.#customBtnColorBlocks.length = 0;
        this.#$monthSelectBtn.appendChild(this.#createTrackedColorBlocks(this.#customBtnColorBlocks));
        const $textSpan = document.createElement('span');
        $textSpan.textContent = 'Custom';
        this.#$monthSelectBtn.appendChild($textSpan);
    }

    #updateDropdownPreview(preset: MonthPreset): void {
        if (!this.#$monthSelectBtn) {
            return;
        }
        this.#$monthSelectBtn.innerHTML = '';
        this.#$monthSelectBtn.appendChild(this.#createColorBlocks(preset.colors));
        const $textContainer = document.createElement('div');
        $textContainer.className = 'preset-text';
        const $monthSpan = document.createElement('div');
        $monthSpan.className = 'preset-month d-block d-md-inline';
        $monthSpan.textContent = preset.month;
        const $separator = document.createElement('div');
        $separator.className = 'd-none d-md-inline mx-1';
        $separator.textContent = '-';
        const $nameSpan = document.createElement('div');
        $nameSpan.className = 'preset-name d-block d-md-inline';
        $nameSpan.textContent = preset.name;
        $textContainer.appendChild($monthSpan);
        $textContainer.appendChild($separator);
        $textContainer.appendChild($nameSpan);
        this.#$monthSelectBtn.appendChild($textContainer);
    }

    #updateCustomMenuToSavedState(): void {
        if (!this.#colorState.savedCustomState) {
            return;
        }
        const colorKeys = ['colorOne', 'colorTwo', 'colorThree'] as const;
        for (let i = 0; i < colorKeys.length; i++) {
            if (this.#customMenuColorBlocks[i]) {
                this.#customMenuColorBlocks[i].style.backgroundColor =
                    `#${this.#colorState.savedCustomState.colors[colorKeys[i]]}`;
            }
        }
    }

    #updateCustomColorBlocks(): void {
        // Only update if in custom mode
        if (!this.#colorState.isCustomMode) {
            return;
        }

        const colors = this.getCurrentColors();
        const colorKeys = ['colorOne', 'colorTwo', 'colorThree'] as const;

        for (let i = 0; i < colorKeys.length; i++) {
            const color = `#${colors[colorKeys[i]]}`;
            if (this.#customMenuColorBlocks[i]) {
                this.#customMenuColorBlocks[i].style.backgroundColor = color;
            }
            if (this.#customBtnColorBlocks[i]) {
                this.#customBtnColorBlocks[i].style.backgroundColor = color;
            }
        }
    }

    // ==================== Private: UI Helpers ====================

    #createColorBlocks(colors: PresetColors): HTMLDivElement {
        const container = document.createElement('div');
        container.className = 'color-blocks';
        for (const colorKey of ['colorOne', 'colorTwo', 'colorThree'] as const) {
            const colorBlock = document.createElement('div');
            colorBlock.className = 'color-block';
            colorBlock.style.backgroundColor = `#${colors[colorKey]}`;
            container.appendChild(colorBlock);
        }
        return container;
    }

    #createTrackedColorBlocks(tracker: HTMLDivElement[]): HTMLDivElement {
        const container = document.createElement('div');
        container.className = 'color-blocks';
        const colors = this.getCurrentColors();
        for (const colorKey of ['colorOne', 'colorTwo', 'colorThree'] as const) {
            const colorBlock = document.createElement('div');
            colorBlock.className = 'color-block';
            colorBlock.style.backgroundColor = `#${colors[colorKey]}`;
            tracker.push(colorBlock);
            container.appendChild(colorBlock);
        }
        return container;
    }
}
